import { Button, Upload, UploadFile } from "antd";
import { InboxOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { memo, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import CheckboxGroup from "src/components/utils/CheckboxGroup";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 5;
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];
const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/mov",
  "video/avi",
  "video/quicktime",
];

export const delicateObjectsOptions = [
  {
    label: "Antiques & collectibles",
    value: "antiques",
    icon: "🏺",
    description: "Vintage items, collectibles, or antique furniture",
    disabled: false,
    checked: false,
  },
  {
    label: "Electronics & appliances",
    value: "electronics",
    icon: "📱",
    description: "Computers, TVs, speakers, kitchen appliances",
    disabled: false,
    checked: false,
  },
  {
    label: "Artwork & paintings",
    value: "artwork",
    icon: "🎨",
    description: "Paintings, prints, sculptures, decorative art",
    disabled: false,
    checked: false,
  },
  {
    label: "Plants & flowers",
    value: "plants",
    icon: "🌱",
    description: "Indoor plants, flower arrangements, terrariums",
    disabled: false,
    checked: false,
  },
  {
    label: "Glassware & ceramics",
    value: "glassware",
    icon: "🥃",
    description: "Crystal, fine china, decorative glass items",
    disabled: false,
    checked: false,
  },
  {
    label: "Musical instruments",
    value: "instruments",
    icon: "🎹",
    description: "Pianos, guitars, violins, drums, etc.",
    disabled: false,
    checked: false,
  },
  {
    label: "Fragile furniture",
    value: "fragile_furniture",
    icon: "🪑",
    description: "Delicate chairs, glass tables, ornate pieces",
    disabled: false,
    checked: false,
  },
  {
    label: "None of these",
    value: "none",
    disabled: false,
    checked: false,
  },
  {
    label: "Others",
    value: "others",
    disabled: false,
    checked: false,
  },
];

const UploadContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const StyledUpload = styled(Upload.Dragger)`
  .ant-upload-drag {
    border: 2px dashed #d9d9d9;
    border-radius: 10px !important;
    background: #fafafa;
    transition: all 0.3s ease;

    &:hover {
      border-color: var(--blue-300);
      background: #f0f8ff;
    }
  }

  .ant-upload-drag-hover {
    border-color: var(--blue-300) !important;
    background: #f0f8ff !important;
  }
`;

const FileList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  background: #fafafa;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const FileName = styled.span`
  font-weight: 500;
  color: #333;
`;

const FileSize = styled.span`
  font-size: 0.875rem;
  color: #999;
`;

const FileActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  color: #666;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    color: var(--blue-300);
  }

  &.delete:hover {
    color: #ff4d4f;
  }
`;

const InstructionsContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const StyledTextarea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => (props.$hasError ? "red" : "#d9d9d9")};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: var(--blue-300);
    box-shadow: 0 0 0 2px rgba(37, 62, 198, 0.1);
  }

  &::placeholder {
    color: #999;
  }
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ErrorText = styled.small`
  color: red;
  display: block;
  margin-top: 0.25rem;
  font-size: 12px;
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-top: 0.25rem;
`;

const StyledLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const OthersContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  gap: 0.5rem;
`;

const OthersTextarea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => (props.$hasError ? "red" : "#d9d9d9")};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: var(--blue-300);
    box-shadow: 0 0 0 2px rgba(37, 62, 198, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

interface FileWithPreview extends File {
  id: string;
  preview?: string;
}

function DelicateObjects({ onSubmit, isSubmitting }) {
  const {
    formState: { errors },
    register,
    watch,
    setValue,
    getValues,
    handleSubmit,
    setError,
    clearErrors,
    control,
  } = useFormContext();

  const [files, setFiles] = useState<UploadFile<FileWithPreview>[]>(() => {
    return getValues("delicateFiles") || [];
  });
  const [delicateObjects, setDelicateObjects] = useState(
    () => delicateObjectsOptions
  );
  const instructions = watch("delicateInstructions") || "";

  const validateFile = useCallback(
    (file: File): string | null => {
      const allowedTypes = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];

      if (!allowedTypes.includes(file.type)) {
        return `File type not supported. Please upload images (JPG, PNG, GIF, WebP) or videos (MP4, MOV, AVI).`;
      }

      if (files.findIndex((f) => f.name === file.name) !== -1) {
        return `File "${file.name}" already exists.`;
      }

      if (file.size > MAX_FILE_SIZE) {
        return `File size too large. Maximum size is 5MB.`;
      }

      return null;
    },
    [files]
  );

  const createFilePreview = (file: File): string | undefined => {
    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return URL.createObjectURL(file);
    }
    return undefined;
  };

  const handleFileUpload = useCallback(
    (info: any) => {
      const { fileList, file } = info;
      const newFile = file;

      const error = validateFile(newFile);
      if (error) {
        setError("delicateFiles", {
          message: error,
        });
        return;
      }

      if (files.length + fileList.length > MAX_FILES) {
        setError("delicateFiles", {
          message: `Maximum ${MAX_FILES} files allowed.`,
        });
        return;
      }

      if (errors.delicateFiles) {
        clearErrors("delicateFiles");
      }

      const fileWithPreview = newFile;
      fileWithPreview.preview = createFilePreview(newFile);

      setFiles((prev) => [...prev, fileWithPreview]);
    },
    [files.length, setError, validateFile, errors.delicateFiles, clearErrors]
  );

  useEffect(() => {
    setValue("delicateFiles", files);
  }, [files, setValue]);

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((f) => f.uid === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.uid !== fileId);
    });
  }, []);

  const handleDelicateObjectsChange = useCallback(
    (value: any) => {
      const updatedDelicateObjects = [...delicateObjects];
      if (value.includes("none")) {
        updatedDelicateObjects.forEach((item) => {
          item.disabled = item.value !== "none";
          item.checked = item.value === "none";
        });
      } else {
        updatedDelicateObjects.forEach((item) => {
          item.disabled = false;
          item.checked = value.includes(item.value);
        });
      }
      setDelicateObjects([...updatedDelicateObjects]);
      setValue(
        "delicateObjects",
        updatedDelicateObjects
          .filter((item) => item.checked && !item.disabled)
          .map((item) => item.value)
      );
    },
    [delicateObjects, setValue]
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const showOthers = delicateObjects.find(
    (item) => item.value === "others" && item.checked && !item.disabled
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        alignItems: "flex-start",
        justifyContent: "center",
        marginBottom: "2rem",
      }}
    >
      <p>
        Please let us know about any delicate, valuable, or fragile items in
        your home that require special attention during cleaning. This helps our
        team provide the safest and most appropriate cleaning service.
      </p>

      <div style={{ width: "100%" }}>
        <StyledLabel>Select delicate objects in your home:</StyledLabel>
        <CheckboxGroup
          options={delicateObjects}
          onChange={handleDelicateObjectsChange}
          control={control}
          id="delicateObjects"
          alwaysShowDescription={true}
        />
        {showOthers && (
          <OthersContainer>
            <StyledLabel htmlFor="delicateOthers">
              Please describe the delicate objects
            </StyledLabel>
            <OthersTextarea
              id="delicateOthers"
              {...register("delicateOthers", {
                required: {
                  value: true,
                  message: "Please describe the delicate objects",
                },
              })}
              placeholder="Please describe the delicate objects that need special care..."
              $hasError={!!errors.delicateOthers}
            />
            {errors.delicateOthers && (
              <ErrorText>{errors.delicateOthers.message}</ErrorText>
            )}
          </OthersContainer>
        )}
      </div>

      <UploadContainer>
        <StyledLabel htmlFor="delicateFiles">
          Upload images or videos of delicate objects (optional)
        </StyledLabel>
        <StyledUpload
          id="delicateFiles"
          name="delicateFiles"
          multiple
          beforeUpload={() => false} // Prevent auto upload
          onChange={handleFileUpload}
          fileList={files}
          showUploadList={false}
          accept={[...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES].join(",")}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined
              style={{ fontSize: "48px", color: "var(--blue-300)" }}
            />
          </p>
          <p
            className="ant-upload-text"
            style={{ fontSize: "14px", fontWeight: "500" }}
          >
            Click or drag files to this area to upload
          </p>
          <p
            className="ant-upload-hint"
            style={{ color: "#666", fontSize: "12px" }}
          >
            Support for images (JPG, PNG, GIF, WebP) and videos (MP4, MOV, AVI).
            Max 5MB per file, up to 5 files total.
          </p>
        </StyledUpload>

        {files.length > 0 && (
          <FileList>
            {files.map((file) => (
              <FileItem key={file.uid}>
                <FileInfo>
                  {file.preview && (
                    <img
                      src={file.preview}
                      alt={file.name}
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                  <div>
                    <FileName>{file.name}</FileName>
                    <FileSize>
                      &nbsp;({formatFileSize(file.size || 0)})
                    </FileSize>
                  </div>
                </FileInfo>
                <FileActions>
                  {file.preview && (
                    <ActionButton
                      type="button"
                      onClick={() => window.open(file.preview, "_blank")}
                      title="Preview"
                    >
                      <EyeOutlined />
                    </ActionButton>
                  )}
                  <ActionButton
                    type="button"
                    onClick={() => removeFile(file.uid)}
                    title="Remove"
                    className="delete"
                  >
                    <DeleteOutlined />
                  </ActionButton>
                </FileActions>
              </FileItem>
            ))}
          </FileList>
        )}

        {errors.delicateFiles && (
          <ErrorText>{errors.delicateFiles.message}</ErrorText>
        )}
      </UploadContainer>

      <InstructionsContainer>
        <StyledLabel htmlFor="delicateInstructions">
          Additional Instructions for Delicate Objects
        </StyledLabel>
        <StyledTextarea
          id="delicateInstructions"
          {...register("delicateInstructions", {
            maxLength: {
              value: 1000,
              message: "Instructions cannot exceed 1000 characters",
            },
          })}
          placeholder="Please provide any specific instructions for handling these delicate objects..."
          $hasError={!!errors.delicateInstructions}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 5,
            width: "100%",
          }}
        >
          {errors.delicateInstructions && (
            <ErrorText>{errors.delicateInstructions.message}</ErrorText>
          )}
          <CharacterCount>{instructions.length}/1000 characters</CharacterCount>
        </div>
      </InstructionsContainer>

      <Button
        onClick={handleSubmit(onSubmit)}
        type="primary"
        form="instructions-form"
        className="next-button w-full"
        value="Complete Instructions"
        disabled={isSubmitting}
        loading={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "Complete Instructions"}
      </Button>
    </div>
  );
}

export default memo(DelicateObjects);
